import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';

export interface StartedBatch {
  batchId: number;
  galleryId: number;
}

export interface CompletedBatch {
  batchId: number;
  galleryId: number;
  succeeded: number;
  totalJobs: number;
  failed: number;
}

export interface CompleteBatchPayload {
  galleryId: number;
  succeeded: number;
  totalJobs: number;
  failed: number;
}

export interface StartedMotion {
  sessionId: string;
  galleryId: number;
}

export interface CompletedMotion {
  sessionId: string;
  galleryId: number;
  status: 'success' | 'failed';
  videoUrl?: string;
  error?: string;
}

export type ActiveBatchesMap = Record<number, { galleryId: number }>;

export const useConstellationGenerationStore = defineStore('constellation-generation', () => {
  const activeBatches = ref<ActiveBatchesMap>({});
  const lastStartedBatch = ref<StartedBatch | null>(null);
  const lastCompletedBatch = ref<CompletedBatch | null>(null);

  const activeMotionSessions = reactive(new Map<string, { galleryId: number }>());
  const lastStartedMotion = ref<StartedMotion | null>(null);
  const lastCompletedMotion = ref<CompletedMotion | null>(null);

  function startBatch(batchId: number, galleryId: number) {
    const numericBatchId = Number(batchId);
    const numericGalleryId = Number(galleryId);
    activeBatches.value = {
      ...activeBatches.value,
      [numericBatchId]: { galleryId: numericGalleryId },
    };
    lastStartedBatch.value = { batchId: numericBatchId, galleryId: numericGalleryId };
  }

  function noteActiveBatch(batchId: number, galleryId: number) {
    const numericBatchId = Number(batchId);
    if (activeBatches.value[numericBatchId]) return;
    activeBatches.value = {
      ...activeBatches.value,
      [numericBatchId]: { galleryId: Number(galleryId) },
    };
  }

  function completeBatch(batchId: number, payload: CompleteBatchPayload) {
    const { galleryId, succeeded, totalJobs, failed } = payload;
    const numericBatchId = Number(batchId);
    const next = { ...activeBatches.value };
    delete next[numericBatchId];
    activeBatches.value = next;
    lastCompletedBatch.value = { batchId: numericBatchId, galleryId, succeeded, totalJobs, failed };
  }

  function startMotion(sessionId: string, galleryId: number) {
    activeMotionSessions.set(sessionId, { galleryId });
    lastStartedMotion.value = { sessionId, galleryId };
  }

  function completeMotion(payload: CompletedMotion) {
    activeMotionSessions.delete(payload.sessionId);
    lastCompletedMotion.value = payload;
  }

  return {
    activeBatches,
    lastStartedBatch,
    lastCompletedBatch,
    startBatch,
    noteActiveBatch,
    completeBatch,
    activeMotionSessions,
    lastStartedMotion,
    lastCompletedMotion,
    startMotion,
    completeMotion,
  };
});
